import { useExpressPayoutMutation } from "Order/Modules/Payment/Api/Mutation/useExpressPayoutMutation";
import { ExpressPayoutConfirmModal } from "Order/Modules/Payment/Components/ExpressPayout/ExpressPayoutConfirmModal";
import { BaseCard } from "Shared/Components/Cards/BaseCard";
import { DoneAll, KeyboardDoubleArrowRight } from "@mui/icons-material";
import { Stack, Typography } from "@mui/material";
import { type FC, useState } from "react";
import { toast } from "sonner";

type Props = {
	orderId: number;
	expressPayoutSent: boolean;
};

export const ExpressPayoutWidget: FC<Props> = (props) => {
	const { orderId, expressPayoutSent } = props;
	const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

	const { mutate: sendExpressPayment } = useExpressPayoutMutation(orderId);

	const handleSubmit = () => {
		sendExpressPayment(undefined, {
			onError: () => {
				toast.error("It seems that you cannot send express payment");
			},
			onSuccess: () => {
				setIsConfirmationModalOpen(false);
			},
		});
	};

	return (
		<>
			<BaseCard
				sx={{
					transition: "background 0.2s ease",
					background: (t) =>
						expressPayoutSent ? t.palette.grey[100] : t.palette.success.light,
					position: "relative",
				}}
			>
				<Stack
					direction="row"
					sx={{
						position: "absolute",
						right: 0,
						top: 0,
						height: "100%",
						width: "100%",
						paddingRight: expressPayoutSent ? "0" : "10%",
						transition: "padding-right 0.2s ease, box-shadow 0.15s ease",
						cursor: "pointer",
						boxShadow: (t) => `inset 0 0 0 0 ${t.palette.success.main}44`,
						...(expressPayoutSent
							? {}
							: {
									"&:hover": {
										paddingRight: "3%",
										boxShadow: (t) =>
											`inset 400px 0 100px 0 ${t.palette.success.main}22`,
									},
									"&:active": {
										paddingRight: "0",
										boxShadow: (t) =>
											`inset 400px 0 5px 0 ${t.palette.success.main}22, inset 400px 0 10px 0 ${t.palette.success.main}33`,
									},
								}),
						...(isConfirmationModalOpen
							? {
									paddingRight: "0",
									boxShadow: (t) =>
										`inset 400px 0 5px 0 ${t.palette.success.main}22, inset 400px 0 10px 0 ${t.palette.success.main}33`,
								}
							: {}),
					}}
					onClick={() => !expressPayoutSent && setIsConfirmationModalOpen(true)}
					columnGap={3}
					justifyContent={"end"}
				>
					<DoneAll
						style={{
							transition: "0.2s ease",
							transform: `scale(1.3) translateX(${expressPayoutSent ? "" : "-"}55%)`,
							opacity: expressPayoutSent ? "0.25" : "0",
						}}
					/>
					<KeyboardDoubleArrowRight
						style={{
							transition: "0.2s ease",
							transform: `scale(1.3) translateX(${expressPayoutSent ? "" : "-"}13%)`,
							opacity: expressPayoutSent ? "0" : "0.25",
						}}
					/>
				</Stack>
				<Typography variant="h5">{`Express payout ${expressPayoutSent ? "sent" : ""}`}</Typography>
			</BaseCard>
			<ExpressPayoutConfirmModal
				isOpen={isConfirmationModalOpen}
				onSubmit={handleSubmit}
				handleClose={() => setIsConfirmationModalOpen(false)}
				isPending={false}
			/>
		</>
	);
};
